import { styled } from 'styled-components'

import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'
import { CommonStrings } from '@b-stock/fe-core/locale'

import useStorefrontUrl from '@/app/buy/seller/[sellerSlug]/useStorefrontUrl'
import { Breadcrumbs } from '@/buyer-portal/components/Breadcrumbs'
import LegacyBpLink from '@/buyer-portal/components/LegacyBpLink'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { getSellerLogo } from '@/helpers/utils'

const BreadcrumbsSectionContainer = styled.div`
  grid-area: breadcrumbs;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.25rem 2rem;
  flex-wrap: 1;
`

const SellerLogoImage = styled.img`
  height: 62px;
`

export const BreadcrumbsSection = ({
  listing,
  showLogo = false,
}: {
  listing: OrchestratedListingDetails
  showLogo?: boolean
}) => {
  const { trackButtonClicked } = useAnalytics()
  const { seller } = listing
  const listingFormattedPrettyId = listing.formattedPrettyId

  const storefrontName = seller.storefront?.name
  const displayName = seller.account.displayName

  const sellerName = storefrontName || displayName || (
    <CommonStrings.NotAvailableAbbr.Component />
  )

  const listingIdLabel = listingFormattedPrettyId ? (
    <FormattedMessage
      id="Auction.details.breadcrumbs.prettyId"
      values={{ listingId: listingFormattedPrettyId }}
    />
  ) : (
    listing.lot.title || <FormattedMessage id="Common.unknown" />
  )

  const logoUrl = getSellerLogo(seller.storefront?.documents)
  const storefrontUrl = useStorefrontUrl(seller.storefront)
  const logo =
    showLogo && logoUrl ? (
      <SellerLogoImage src={logoUrl} alt={`Logo for ${storefrontName}`} />
    ) : null

  const handleStorefrontLogoClick = () => {
    trackButtonClicked({
      screenName: 'listing_detail_page',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      listing_id: listing.lot._id,
      seller_account_id: listing.accountId,
      storefront_name: listing.seller.storefront?.name,
      seller_display_name: listing.seller.account.displayName || '',
      buttonName: 'storefront_logo',
    })
  }

  const handleStorefrontNavigationCrumbClick = () => {
    trackButtonClicked({
      screenName: 'listing_detail_page',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      listing_id: listing.lot._id,
      seller_account_id: listing.accountId,
      storefront_name: listing.seller.storefront?.name,
      seller_display_name: listing.seller.account.displayName || '',
      buttonName: 'storefront_navigation_crumb',
    })
  }

  const handleAllListingNavigationCrumbClick = () => {
    trackButtonClicked({
      screenName: 'listing_detail_page',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      listing_id: listing.lot._id,
      seller_account_id: listing.accountId,
      storefront_name: listing.seller.storefront?.name,
      seller_display_name: listing.seller.account.displayName || '',
      buttonName: 'all_listing_navigation_crumb',
    })
  }

  return (
    <BreadcrumbsSectionContainer>
      {logo && storefrontUrl ? (
        <LegacyBpLink href={storefrontUrl} onClick={handleStorefrontLogoClick}>
          {logo}
        </LegacyBpLink>
      ) : (
        logo
      )}
      <Breadcrumbs
        items={[
          {
            label: (
              <FormattedMessage id="Auction.details.breadcrumbs.allAuctions" />
            ),
            url: '/all-auctions',
            handleClick: handleAllListingNavigationCrumbClick,
          },
          {
            label: sellerName,
            url: storefrontUrl ?? '/',
            isCurrentPage: !storefrontUrl,
            handleClick: handleStorefrontNavigationCrumbClick,
          },
        ]}
        currentPageLabel={listingIdLabel}
      />
    </BreadcrumbsSectionContainer>
  )
}
